import { useCallback, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Loader2, FileText, Mail, CheckCircle2, Circle, Download,
  FileSignature, RotateCcw, Shield,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  todo: { label: "À faire", cls: "bg-white/5 text-slate-400" },
  generated: { label: "Généré", cls: "bg-amber-500/15 text-amber-400" },
  sent: { label: "Envoyé", cls: "bg-blue-500/15 text-blue-400" },
  done: { label: "Terminé", cls: "bg-green-500/15 text-green-400" },
};

// Dossier d'inscription Qualiopi — checklist guidée par stagiaire
export const EnrollmentDossier = ({ enrollmentId, api, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/lms/enrollments/${enrollmentId}/dossier`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  // eslint-disable-next-line
  }, [enrollmentId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [enrollmentId]);

  const generate = async (step) => {
    setBusy(step.key);
    try {
      const r = await api.post(`/lms/enrollments/${enrollmentId}/dossier/steps/${step.key}/generate`, {});
      let msg = `${r.data.generated.length} document(s) généré(s)`;
      if (r.data.email_sent) msg += " · email envoyé au stagiaire";
      if (r.data.sign_url && !r.data.email_sent) msg += " · lien de signature créé";
      toast.success(msg);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };

  const send = async (step) => {
    setBusy(step.key);
    try {
      await api.post(`/lms/enrollments/${enrollmentId}/dossier/steps/${step.key}/send`);
      toast.success("Email envoyé au stagiaire");
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };

  const complete = async (step, undo) => {
    setBusy(step.key);
    try {
      await api.post(`/lms/enrollments/${enrollmentId}/dossier/steps/${step.key}/complete`, { undo });
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };

  const download = (docId) => window.open(`${BACKEND}/api/lms/qualiopi/documents/${docId}/download`, "_blank");

  const phases = data ? [...new Set(data.steps.map(s => s.phase))] : [];

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl bg-[#0b0811] border border-white/10 text-white" data-testid="enrollment-dossier-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#f7c935]" />
            Dossier d'inscription Qualiopi
          </DialogTitle>
        </DialogHeader>
        {!data ? <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-10" /> : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-bold" data-testid="dossier-student-name">{data.enrollment.student_name}</div>
                  <div className="text-xs text-slate-400">{data.enrollment.student_email} · {data.session.title} ({data.session.start_date} → {data.session.end_date})</div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${data.completion_pct >= 100 ? "text-green-400" : "text-[#f7c935]"}`} data-testid="dossier-completion-pct">{data.completion_pct}%</div>
                  <div className="text-[10px] text-slate-500">{data.done_count}/{data.total_steps} étapes</div>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-2">
                <div className={`h-2 rounded-full transition-all ${data.completion_pct >= 100 ? "bg-green-400" : "bg-[#f7c935]"}`} style={{ width: `${data.completion_pct}%` }} />
              </div>
              {data.completion_pct >= 100 && (
                <div className="mt-2 text-xs text-green-400 flex items-center gap-1" data-testid="dossier-conforme-badge">
                  <CheckCircle2 className="w-3 h-3" /> Dossier complet — conforme Qualiopi
                </div>
              )}
            </div>

            {phases.map(phase => (
              <div key={phase}>
                <div className="text-[10px] uppercase text-slate-500 font-bold mb-2">{phase}</div>
                <div className="space-y-2">
                  {data.steps.filter(s => s.phase === phase).map(step => {
                    const meta = STATUS_META[step.status] || STATUS_META.todo;
                    const isDone = step.status === "done";
                    return (
                      <div key={step.key} className="bg-[#14101c] border border-white/10 rounded-lg p-3" data-testid={`dossier-step-${step.key}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            {isDone ? <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" /> : <Circle className="w-5 h-5 text-slate-600 mt-0.5 shrink-0" />}
                            <div className="min-w-0">
                              <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                                {step.label}
                                {step.signature && <FileSignature className="w-3 h-3 text-purple-400" />}
                                <span className={`text-[10px] px-2 py-0.5 rounded ${meta.cls}`} data-testid={`dossier-step-status-${step.key}`}>{isDone && step.signature ? "Signé" : meta.label}</span>
                              </div>
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {step.indicators.map(i => (
                                  <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-[#f7c935]/10 text-[#f7c935]">Ind. {i}</span>
                                ))}
                                {step.auto_email && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">Email auto</span>}
                              </div>
                              {step.docs.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {step.docs.map(d => (
                                    <button key={d.document_id} onClick={() => download(d.document_id)}
                                      className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-[#f7c935]"
                                      data-testid={`dossier-doc-${d.document_id}`}>
                                      <FileText className="w-3 h-3" /> {d.doc_name}
                                      {d.status === "signed" && <span className="text-green-400">✓ signé</span>}
                                      <Download className="w-3 h-3 opacity-60" />
                                    </button>
                                  ))}
                                </div>
                              )}
                              {step.email_sent_at && <div className="text-[10px] text-slate-500 mt-1">Email envoyé le {new Date(step.email_sent_at).toLocaleDateString("fr-FR")}</div>}
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 shrink-0">
                            {step.doc_types.length > 0 && (
                              <Button size="sm" onClick={() => generate(step)} disabled={busy === step.key}
                                className="bg-[#f7c935] text-[#14101c] font-bold text-xs h-7"
                                data-testid={`dossier-generate-${step.key}`}>
                                {busy === step.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
                                {step.docs.length > 0 ? "Régénérer" : "Générer"}{step.auto_email ? " + email" : ""}
                              </Button>
                            )}
                            {step.docs.length > 0 && (
                              <Button size="sm" variant="ghost" onClick={() => send(step)} disabled={busy === step.key}
                                className="text-blue-300 text-xs h-7" data-testid={`dossier-send-${step.key}`}>
                                <Mail className="w-3 h-3 mr-1" /> Envoyer
                              </Button>
                            )}
                            {isDone ? (
                              <Button size="sm" variant="ghost" onClick={() => complete(step, true)} disabled={busy === step.key}
                                className="text-slate-500 text-xs h-7" data-testid={`dossier-undo-${step.key}`}>
                                <RotateCcw className="w-3 h-3 mr-1" /> Annuler
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => complete(step, false)} disabled={busy === step.key}
                                className="text-green-400 text-xs h-7" data-testid={`dossier-complete-${step.key}`}>
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Marquer fait
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnrollmentDossier;
